import {
    map,
    isArray,
    filter,
    split,
    uniqBy,
} from 'lodash';
import {Point_At_Distance_And_Bearing2} from "../../common/utils/LatLongUtils";
import { SEARCH_TYPE_FILTER_IDS, SEARCH_TYPE_VALUE_BY_ID } from './filters/searchTypeFilterIds';
import { UX_FILTER_EXPANSIONS, isUxFilterSentinel } from '../../common/metadata/uxFilterExpansions';

// Territory "Active Licenses Only" (TCU 2026.02.0) is a NOT-A-FILTER modifier:
// it emits no clause of its own. When on, it re-scopes the LICENSED_IN /
// LICENSED_BY dropdowns from the all-licenses cube columns to their
// active-license counterparts. (The Compliance "Active Licenses Only" is a
// different mechanism — a standalone company_license_active_license_cnt gate.)
const TERRITORY_ACTIVE_LICENSES_ONLY_ID = 'TERRITORY_ACTIVE_LICENSES_ONLY';
const TERRITORY_ALO_FIELD_SWAP = {
    LICENSED_IN: 'company_license_active_license_regions',
    LICENSED_BY: 'company_license_active_license_source_labels',
};

// "Include Contractors without a Trade" is a sibling modifier on the trade
// selection (see post-processing in parseDynamicCubeFilter), not a standalone
// filter when a trade is also selected.
const INCLUDE_CONTRACTORS_WITHOUT_A_TRADE_ID = 'INCLUDE_CONTRACTORS_WITHOUT_A_TRADE';

const isFilterOn = (value) =>
    value !== undefined &&
    value !== null &&
    value !== false &&
    value !== '' &&
    String(value).toLowerCase() !== 'false';

export const parseCubeFilter = (filters, table = 'Company') => {
    const filterToDeconstruct = filters.find((f) => f.id === 'COMPANY_ADDRESS_CITY_REGION_FORMATTED');
    if (filterToDeconstruct) {
        const filterToDeconstructIndex = filters.findIndex((f) => f.id === 'COMPANY_ADDRESS_CITY_REGION_FORMATTED');
        filters.splice(filterToDeconstructIndex, 1);
        filters.push({
            id: 'SUBSTITUTE_CITY_FILTER',
            display: 'Company City',
            field: [
                'CompanyContractorUniverse.company_address_city'
            ],
            operatorDisplay: 'in',
            operatorId: 'equals',
            component: 'combobox',
            fieldDisplay: '[company_address_city]',
            hide: true,
            value: uniqBy(filterToDeconstruct.value.map((v) => {
                return {id: v.id.split(',')[0].trim()}
            }), 'id')
        });
        filters.push({
            id: 'SUBSTITUTE_REGION_FILTER',
            display: 'Company Region',
            field: [
                'CompanyContractorUniverse.company_address_region'
            ],
            operatorDisplay: 'in',
            operatorId: 'equals',
            component: 'combobox',
            fieldDisplay: '[company_address_region]',
            hide: true,
            value: uniqBy(filterToDeconstruct.value.map((v) => {
                return {id: v.id.split(',')[1].trim()}
            }), 'id')
        });
    }

    const replaceTableName = (field) => {
        if (isArray(field)) {
            return map(field, f => f.replace('Company', table));
        }
        return field.replace('Company', table);
    };
    const _filters = map(filters, (ft) => ({
        ...ft,
        // Use the filter's `field` (the cube member path, e.g.
        // CompanyContractorUniverse.company_address_postal_code), NOT `ft.id`
        // (the UI filter id, e.g. COMPANY_POSTAL_CODE). `ft.id` is not a valid
        // cube member, which silently broke every legacy / old-saved-search
        // filter routed through parseCubeFilter.
        field: replaceTableName(ft.field)
    }));
    return map(_filters, (ft) => {
        if (isArray(ft.field) && ft.field.length > 0) {
            if (ft.operatorId.toLowerCase() === 'multiple_boolean') {
                return _parseFilter(ft);
            }
            return (
                {
                    or: map(ft.field, _field => _parseFilter({
                        ...ft,
                        field: _field
                    }))
                }
            );
        }
        return _parseFilter(ft);
    });

};

const _parseFilter = (ft) => {
    const operator = ft.operatorId ?? ft.operatorOverride;
    if (operator.toLowerCase() === 'between') {
        return ({
            and: [
                {
                    member: ft.field,
                    operator: 'gte',
                    values: [`${ft.value.min}`]
                },
                {
                    member: ft.field,
                    operator: 'lte',
                    values: [`${ft.value.max}`]
                }
            ]
        });
    }
    if (operator.toLowerCase() === 'equals') {
        if (ft.rangeField) {
            const rangeValues = filter(ft.value, v => (v.min || v.max));
            const rangeQuery = map(rangeValues, v => {
                if (v.min && v.max) {
                    return ({
                        and: [
                            {
                                member: ft.rangeField,
                                operator: 'gte',
                                values: [`${v.min}`]
                            },
                            {
                                member: ft.rangeField,
                                operator: 'lte',
                                values: [`${v.max}`]
                            }
                        ]
                    });
                }
                if (v.min) {
                    return (
                        {
                            member: ft.rangeField,
                            operator: 'gte',
                            values: [`${v.min}`]
                        }
                    );
                }

                if (v.max) {
                    return (
                        {
                            member: ft.rangeField,
                            operator: 'lte',
                            values: [`${v.max}`]
                        }
                    );
                }

            });
            rangeQuery.push(
                {
                    member: ft.field,
                    operator: operator,
                    values: ft.value && isArray(ft.value) ? map(ft.value, v => `${v.id}`) : [`${ft.value}`]
                });
            return (
                {
                    or: rangeQuery
                }
            )
        }
        return (
            {
                member: ft.field,
                operator: operator,
                values: ft.value && isArray(ft.value) ? map(ft.value, v => `${v.id}`) : [`${ft.value}`]
            })
    }

    if (operator.toLowerCase() === 'notequals') {
        return (
            {
                member: ft.field,
                operator: operator,
                values: ft.value && isArray(ft.value) ? map(ft.value, v => `${v.id}`) : [`${ft.value}`]
            })
    }

    if (operator.toLowerCase() === 'set') {
        return (
            {
                member: ft.field,
                operator: operator
            })
    }

    if (operator.toLowerCase() === 'multiple_boolean') {
        const isUnknownSet = filter(ft.value, v => v.unknown === true);
        const selectedValues = map(ft.value, v => v.id);
        const fieldsSet = filter(ft.field, (_field) => {
            const tableAndColumn = _field.split('.');
            const column = tableAndColumn && tableAndColumn.length === 2 ? tableAndColumn[1] : null;
            return selectedValues.includes(column);
        });
        const querySet = map(fieldsSet, field => {
            return (
                {
                    member: field,
                    operator: 'set'
                }
            );
        });
        if (isUnknownSet && isUnknownSet.length > 0) {
            const queryNotSet = map(ft.field, field => {
                return (
                    {
                        member: field,
                        operator: 'notSet'
                    }
                );
            });
            querySet.push(({
                and: queryNotSet
            }))
        }

        return ({
            or: querySet
        });
    }

    if (operator.toLowerCase() === 'lat_long_radius') {
        const latLongField = split(ft.field, ',');
        const {
            latitude,
            longitude,
            radius
        } = ft.value;
        const latLongEast = Point_At_Distance_And_Bearing2(latitude, longitude, `${radius} mi`, '90');
        const latLongSouth = Point_At_Distance_And_Bearing2(latitude, longitude, `${radius} mi`, '180');
        const latLongWest = Point_At_Distance_And_Bearing2(latitude, longitude, `${radius} mi`, '270');
        const latLongNorth = Point_At_Distance_And_Bearing2(latitude, longitude, `${radius} mi`, '360');
        const minLat = split(latLongSouth, ',')[0];
        const maxLat = split(latLongNorth, ',')[0];
        const minLong = split(latLongWest, ',')[1];
        const maxLong = split(latLongEast, ',')[1];
        return (
            {
                and: [{
                    member: latLongField[0],
                    operator: 'gte',
                    values: [minLat]
                }, {
                    member: latLongField[0],
                    operator: 'lte',
                    values: [maxLat]
                }, {
                    member: latLongField[1],
                    operator: 'gte',
                    values: [minLong]
                }, {
                    member: latLongField[1],
                    operator: 'lte',
                    values: [maxLong]
                }
                ]
            })
    }
    if (operator === 'contains' && Array.isArray(ft.value) && ft.value.length > 0 && ft.value[0].id) {
        const operands = map(ft.value, v => v.id);
        return (
            {
                member: ft.field,
                operator: operator,
                values: operands
            }
        )

    }
    if (operator === 'notContains' && Array.isArray(ft.value) && ft.value.length > 0 && ft.value[0].id) {
        const operands = map(ft.value, v => v.id);
        return (
            {
                member: ft.field,
                operator: operator,
                values: operands
            }
        )

    }
    if (ft.operand) {
        return (
            {
                member: ft.field,
                operator: operator,
                values: [ft.operand]
            }
        )
    }

    return (
        {
            member: ft.field,
            operator: operator,
            values: ft.value && isArray(ft.value) ? map(ft.value, v => `${v}`) : [ft.value]
        }
    )
};

// The cube prefix MUST match the one used to build the query's filters. Pass the
// cubeName resolved for the active universe (e.g. TBDContractorUniverseCompanyExportCubeJS
// for the Contractor Universe) so dimensions and filters target the same cube — mixing
// cubes in one query makes Cube.js throw "Can't find join path to join ...". When no
// cubeName is supplied we fall back to the legacy Business-Universe default.
export const parseDimension = (requestType, cubeName) => {
    const isCompany = requestType === 'SEARCH_COMPANY' || requestType === 'EXPORT_COMPANY';
    const namespace = cubeName || (isCompany ? 'CompanyContractorUniverse' : 'ContactContractorUniverse');
    const columns = isCompany
        ? [
            'company_identity',
            'company_name',
            'company_class_primary_trade',
            'company_class_primary_trade_score',
            'company_url',
            'company_address_delivery_line_1',
            'company_phone',
            'company_email',
        ]
        : [
            'contact_identity',
            'contact_name_given_name',
            'contact_name_surname',
            'contact_title',
            'contact_phone',
            'contact_email',
            'company_name',
            'company_class_primary_trade',
            'company_class_primary_trade_score',
            'company_url',
            'company_address_delivery_line_1',
            'company_phone',
            'company_email',
        ];
    return columns.map(col => `${namespace}.${col}`);
}

export const parseOrgConfigFilter = (filters, config, table = 'Company', accuracyScore = null, cubeNamespace = null) => {
    const _filters = [];
    filters.forEach(f => {
        _filters.push(f);
    });

    if (!config) {
        return filters;
    }

    const companyScoreField = cubeNamespace
        ? `${cubeNamespace}.company_score`
        : 'CompanyContractorUniverse.company_score';
    const contactScoreField = cubeNamespace
        ? `${cubeNamespace}.contact_score`
        : 'ContactContractorUniverse.contact_score';

    if (config.scores || accuracyScore) {
        if (table === 'Company') {
            const score = accuracyScore ? accuracyScore.minScore : config.scores.companyScore;
            _filters.push({
                id: 'LOCKED_COMPANY_SCORE',
                display: 'Company Score',
                field: companyScoreField,
                operatorDisplay: 'between',
                operatorId: 'between',
                component: 'rangeslider',
                value: {
                    min: score,
                    max: 1
                },
                step: 0.01,
                fieldDisplay: 'company_score'
            });
        } else {
            const score = accuracyScore ? accuracyScore.minScore : config.scores.contactScore;
            _filters.push({
                id: 'LOCKED_CONTACT_SCORE',
                display: 'Contact Score',
                field: contactScoreField,
                operatorDisplay: 'between',
                operatorId: 'between',
                component: 'rangeslider',
                value: {
                    min: score,
                    max: 1
                },
                step: 0.01,
                fieldDisplay: 'company_score'
            });
        }
    }
    return _filters;
};

export const parseDynamicCubeFilter = (uiFilters, cubeNamespace) => {
    if (!uiFilters || !Array.isArray(uiFilters)) return [];

    const hasActiveSearchText = uiFilters.some(f =>
        f.schemaProperty === 'search_text'
        && f.schemaCube === 'TBDContractorUniverseSearchIndex'
        && f.value !== undefined && f.value !== null && f.value !== ''
    );
    const explicitlyOffSearchTypeIds = new Set(
        uiFilters
            .filter(f => SEARCH_TYPE_FILTER_IDS.has(f.id) && (f.value === false || String(f.value).toLowerCase() === 'false'))
            .map(f => f.id)
    );
    const activeSearchTypeIds = [...SEARCH_TYPE_FILTER_IDS].filter(id => !explicitlyOffSearchTypeIds.has(id));
    const allSearchTypesActive = activeSearchTypeIds.length === SEARCH_TYPE_FILTER_IDS.size;

    // Territory "Active Licenses Only" modifier: re-scope LICENSED_IN /
    // LICENSED_BY to the active-license columns when the checkbox is on.
    const territoryActiveLicensesOnly = uiFilters.some(
        f => f.id === TERRITORY_ACTIVE_LICENSES_ONLY_ID && isFilterOn(f.value)
    );

    const includeWithoutTrade = uiFilters.some(
        f => f.id === INCLUDE_CONTRACTORS_WITHOUT_A_TRADE_ID && isFilterOn(f.value)
    );

    const filtersToProcess = [];

    uiFilters.forEach(f => {
        if (SEARCH_TYPE_FILTER_IDS.has(f.id)) return;
        // NOT-A-FILTER row — modifies its siblings, never emits a clause itself.
        if (f.id === TERRITORY_ACTIVE_LICENSES_ONLY_ID) return;
        // When unchecked, this must emit nothing — its `notSet` operatorOverride
        // would otherwise restrict results to no-trade contractors even when off.
        // When checked it flows through normally to emit the notSet clause, which
        // the grouping step below may fold into an OR with the trade selection.
        if (f.id === INCLUDE_CONTRACTORS_WITHOUT_A_TRADE_ID && !isFilterOn(f.value)) return;
        if (territoryActiveLicensesOnly && TERRITORY_ALO_FIELD_SWAP[f.id]) {
            filtersToProcess.push({
                ...f,
                schemaProperty: TERRITORY_ALO_FIELD_SWAP[f.id],
            });
            return;
        }
        if (f.id === 'COMPANY_ADDRESS_CITY_REGION_FORMATTED' || f.schemaProperty === 'company_address_city_region_formatted') {
            const cityValues = f.value.map(v => {
                const text = v.id || v;
                return text.split(',')[0].trim();
            });

            const regionValues = f.value.map(v => {
                const text = v.id || v;
                const parts = text.split(',');
                return parts.length > 1 ? parts[1].trim() : '';
            }).filter(Boolean);

            filtersToProcess.push({
                schemaProperty: 'company_address_city',
                operatorId: 'equals',
                value: [...new Set(cityValues)],
                multiple: true
            });

            filtersToProcess.push({
                schemaProperty: 'company_address_region',
                operatorId: 'equals',
                value: [...new Set(regionValues)],
                multiple: true
            });

        } else if (f.operatorId === 'geoRadius' || f.component === 'geoRadius') {
            filtersToProcess.push(f);
        } else {
            filtersToProcess.push(f);
        }
    });

    const validFilters = filtersToProcess.filter(f => {
        if (f.operatorId === 'geoRadius' || f.component === 'geoRadius') {
            return f.value && f.value.latitude && f.value.longitude && f.value.radius;
        }

        const hasSchema = Boolean(f.schemaProperty && f.schemaProperty.trim() !== '');
        const hasValue = f.value !== undefined && f.value !== null && f.value !== '';
        const isNonEmptyArray = Array.isArray(f.value) ? f.value.length > 0 : true;
        return hasSchema && hasValue && isNonEmptyArray;
    });

    const regularFilters = validFilters.map(f => {
        if (f.operatorId === 'geoRadius' || f.component === 'geoRadius') {
            const { latitude, longitude, radius } = f.value;

            const lat = parseFloat(latitude);
            const lon = parseFloat(longitude);
            const rad = parseFloat(radius);

            const latDegreePerMile = 1 / 69.0;
            const lonDegreePerMile = 1 / (69.0 * Math.cos(lat * Math.PI / 180));

            const minLat = lat - (rad * latDegreePerMile);
            const maxLat = lat + (rad * latDegreePerMile);
            const minLon = lon - (rad * Math.abs(lonDegreePerMile));
            const maxLon = lon + (rad * Math.abs(lonDegreePerMile));

            const latField = `${cubeNamespace}.company_address_latitude`;
            const lonField = `${cubeNamespace}.company_address_longitude`;

            return {
                "and": [
                    { member: latField, operator: 'gte', values: [String(minLat)] },
                    { member: latField, operator: 'lte', values: [String(maxLat)] },
                    { member: lonField, operator: 'gte', values: [String(minLon)] },
                    { member: lonField, operator: 'lte', values: [String(maxLon)] }
                ]
            };
        }

        let operator = f.operatorId || 'equals';
        let values = [];

        if (Array.isArray(f.value)) {
            values = f.value.map(v => String(v.id || v));
        } else if (f.value !== null && typeof f.value === 'object' && f.value.id) {
            values = [String(f.value.id)];
        } else {
            values = [String(f.value)];
        }

        if (f.filterType) {
            switch (f.filterType) {
                case 'CONTAINS':
                    operator = 'contains';
                    break;
                case 'NOT_CONTAINS':
                    operator = 'notContains';
                    break;
                case 'HAS_VALUE':
                    operator = f.operatorOverride
                        || (f.value === true || String(f.value).toLowerCase() === 'true' ? 'set' : 'notSet');
                    values = Array.isArray(f.valuesOverride) ? f.valuesOverride : undefined;
                    break;
                case 'DROP_DOWN':
                    // Cube.js operators are case-sensitive (e.g. 'notContains', 'notSet') —
                    // don't lowercase operatorOverride, just use it as-is.
                    operator = f.operatorOverride || 'equals';
                    break;
                case 'GREATER_THAN':
                    operator = 'gt';
                    break;
                default:
                    operator = 'equals';
            }
        }

        if (isUxFilterSentinel(f.schemaProperty)) {
            const expansion = UX_FILTER_EXPANSIONS[f.schemaProperty];
            if (!expansion) {
                return null;
            }
            const effectiveOperator = expansion.operator || operator;
            const effectiveValues =
                expansion.values !== undefined ? expansion.values : values;
            return {
                [expansion.combinator]: expansion.parts.map((part) => {
                    const child = {
                        member: `${f.schemaCube || cubeNamespace}.${part.schemaProperty}`,
                        operator: effectiveOperator,
                    };
                    if (effectiveValues !== undefined && effectiveValues.length > 0) {
                        child.values = effectiveValues;
                    }
                    return child;
                }),
            };
        }

        const baseFilter = {
            member: `${f.schemaCube || cubeNamespace}.${f.schemaProperty}`,
            operator: operator
        };

        if (values !== undefined && values.length > 0) {
            baseFilter.values = values;
        }

        if (f.valuesOverride !== undefined && f.valuesOverride.length > 0) {
            baseFilter.values = f.valuesOverride;
        }

        return baseFilter;

    }).filter(Boolean);

    // ─── Search type scope filter ─────────────────────────────────────────────
    if (hasActiveSearchText && !allSearchTypesActive && activeSearchTypeIds.length > 0) {
        regularFilters.push({
            member: 'TBDContractorUniverseSearchIndex.search_type',
            operator: 'equals',
            // Submit the cube's expected literals (from each filter's
            // `filterValue`), not the internal filter ids.
            values: activeSearchTypeIds.map(id => SEARCH_TYPE_VALUE_BY_ID[id]),
        });
    }

    // ─── Include Contractors without a Trade → OR with trade selection ──
    // When the checkbox is on AND a trade is selected, the "no trade assigned"
    // set must be ORed with the trade match so no-trade contractors don't fall
    // off. The same-member grouping below cannot express this on its own,
    // because Primary Trade lives on a different cube column
    // (company_class_primary_trade) than the notSet clause
    // (company_class_trades). Checked with no trade selected, the notSet clause
    // is left to stand alone ("only contractors without a trade").
    const consumedByTradeOr = new Set();
    let tradeOrClause = null;
    if (includeWithoutTrade) {
        const notSetClause = regularFilters.find(
            c => c.member && c.member.endsWith('.company_class_trades') && c.operator === 'notSet'
        );
        // Primary Trade (equals) and Trades (contains) are real selections;
        // ANY_ASSIGNED_TRADE's `set` is deliberately excluded.
        const tradeSelections = regularFilters.filter(
            c => c.member && (
                c.member.endsWith('.company_class_primary_trade')
                || (c.member.endsWith('.company_class_trades') && c.operator === 'contains')
            )
        );
        if (notSetClause && tradeSelections.length > 0) {
            const tradeConstraint = tradeSelections.length === 1
                ? tradeSelections[0]
                : { and: tradeSelections };
            tradeOrClause = { or: [tradeConstraint, notSetClause] };
            consumedByTradeOr.add(notSetClause);
            tradeSelections.forEach(c => consumedByTradeOr.add(c));
        }
    }

    // ─── Group filters at same member → OR ──────────────────────
    // Complex filters (and/or objects) not group
    const simpleFilters = [];   // { member, operator, values }
    const complexFilters = [];  // { and: [...] } | { or: [...] }

    regularFilters.forEach(f => {
        if (consumedByTradeOr.has(f)) return;
        if (f.member) {
            simpleFilters.push(f);
        } else {
            complexFilters.push(f);
        }
    });

    // group by member key
    const memberGroups = {};
    simpleFilters.forEach(f => {
        if (!memberGroups[f.member]) memberGroups[f.member] = [];
        memberGroups[f.member].push(f);
    });

    const groupedSimpleFilters = Object.values(memberGroups).map(group => {
        if (group.length > 1) {
            return { or: group };
        }
        return group[0];
    });

    const grouped = [...complexFilters, ...groupedSimpleFilters];
    if (tradeOrClause) grouped.push(tradeOrClause);
    return grouped;
};
